import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

export function useSavedProperties() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: savedPropertyIds = [], isLoading } = useQuery({
    queryKey: ["saved-property-ids", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("saved_properties")
        .select("property_id")
        .eq("user_id", user.id);
      if (error) throw error;
      return data.map((s) => s.property_id);
    },
    enabled: !!user,
  });

  const toggleSave = useMutation({
    mutationFn: async (propertyId: string) => {
      if (!user) throw new Error("Must be logged in");

      const isSaved = savedPropertyIds.includes(propertyId);

      if (isSaved) {
        const { error } = await supabase
          .from("saved_properties")
          .delete()
          .eq("user_id", user.id)
          .eq("property_id", propertyId);
        if (error) throw error;
        return { action: "removed" as const };
      } else {
        const { error } = await supabase
          .from("saved_properties")
          .insert({ user_id: user.id, property_id: propertyId });
        if (error) throw error;
        return { action: "added" as const };
      }
    },
    onSuccess: ({ action }) => {
      queryClient.invalidateQueries({ queryKey: ["saved-property-ids"] });
      queryClient.invalidateQueries({ queryKey: ["saved-properties"] });
      toast({
        title: action === "added" ? "Property Saved" : "Property Removed",
        description:
          action === "added"
            ? "Added to your saved properties."
            : "Removed from your saved properties.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update saved properties.",
        variant: "destructive",
      });
    },
  });

  const isSaved = (propertyId: string) => savedPropertyIds.includes(propertyId);

  return {
    savedPropertyIds,
    isLoading,
    isSaved,
    toggleSave: toggleSave.mutate,
    isToggling: toggleSave.isPending,
  };
}
